import api from '@common/api';
import session from '@common/session';
import { MonthlyStatement, TaxDocumentGroup } from '@services/appsync/types';
import { createStore, useStore } from 'zustand';

interface DocumentsModelProps {
  taxDocuments: TaxDocumentGroup[];
}

interface DocumentsModelActions {
  fetchMonthlyStatement: (
    month: string,
  ) => Promise<{ success: boolean; error?: string; monthlyStatement?: MonthlyStatement }>;
  fetchTaxDocuments: () => Promise<{ success: boolean; error?: string; taxDocuments?: TaxDocumentGroup[] }>;
}

export const DocumentsModel = createStore<DocumentsModelProps & DocumentsModelActions>((set) => ({
  fetchMonthlyStatement: async (month) => {
    const investorID = session.get()?.investorId;
    if (!investorID) return { error: 'No Investor Session', success: false };

    const monthStringRegex = /^\d{4}-(0[1-9]|1[0-2])$/;

    if (!monthStringRegex.test(month)) return { error: 'Invalid month format', success: false };

    try {
      const monthlyStatement = await api.getMonthlyStatement({ investor_account_id: investorID, month_ym: month });
      return monthlyStatement
        ? { monthlyStatement: monthlyStatement, success: true }
        : { error: 'Unable to get monthly statement', success: false };
    } catch {
      return { error: 'Unable to get monthly statement', success: false };
    }
  },
  fetchTaxDocuments: async () => {
    const investorID = session.get()?.investorId;
    if (!investorID) return { error: 'No Investor Session', success: false };
    const taxDocuments: TaxDocumentGroup[] =
      (await api.getInvestorTaxDocuments({ investor_account_id: investorID })) ?? [];

    set({ taxDocuments });
    return { success: true, taxDocuments };
  },
  taxDocuments: [],
}));

export function useDocumentsModel(): DocumentsModelProps & DocumentsModelActions;
export function useDocumentsModel<T>(
  selector: (state: DocumentsModelProps & DocumentsModelActions) => T,
  equals?: (a: T, b: T) => boolean,
): T;
export function useDocumentsModel<T>(
  selector?: (state: DocumentsModelProps & DocumentsModelActions) => T,
  equals?: (a: T, b: T) => boolean,
) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return useStore(DocumentsModel, selector!, equals);
}
